/**
 * Audit Logging Service
 * HIPAA-compliant audit logging implementation
 *
 * Features:
 * - 6-year retention policy
 * - Tamper-proof log chain with SHA-256 hashing
 * - Batch logging for performance
 * - Encrypted audit logs
 * - Integrity verification
 */

import {
  AuditLogEntry,
  AuditLogLevel,
  AuditLogFilters,
  AuditLogStats,
  AuditRetentionConfig,
  AuditLogBatch,
  PHIAccessEvent,
  PHIAccessAction,
  AuditResourceType,
  PHIClassification,
} from '../types/audit.types';

/**
 * Audit log service error class
 */
export class AuditLogError extends Error {
  constructor(
    message: string,
    public code: string,
    public originalError?: unknown
  ) {
    super(message);
    this.name = 'AuditLogError';
  }
}

/**
 * Audit log service configuration
 */
interface AuditLogConfig {
  /** Whether audit logging is enabled */
  enabled: boolean;

  /** Retention configuration */
  retention: AuditRetentionConfig;

  /** Batch size for performance optimization */
  batchSize: number;

  /** Flush interval for batch writes (ms) */
  flushInterval: number;

  /** Whether to include browser metadata */
  includeBrowserMetadata: boolean;
}

/**
 * Default audit log configuration
 * Based on .env.example settings
 */
const DEFAULT_CONFIG: AuditLogConfig = {
  enabled: ((window as any).env?.VITE_AUDIT_LOG_ENABLED || import.meta.env.VITE_AUDIT_LOG_ENABLED) === 'true',
  retention: {
    retentionDays: parseInt((window as any).env?.AUDIT_LOG_RETENTION_DAYS || import.meta.env.AUDIT_LOG_RETENTION_DAYS || '2555', 10), // 6 years
    archiveBeforeDelete: true,
    encryptArchive: true,
  },
  batchSize: 50,
  flushInterval: 5000, // 5 seconds
  includeBrowserMetadata: true,
};

/**
 * In-memory audit log store (will be replaced with backend API)
 * For now, we use localStorage for development
 */
class AuditLogStore {
  private static STORAGE_KEY = 'charti_audit_logs';
  private static LAST_HASH_KEY = 'charti_audit_last_hash';

  /**
   * Get all audit logs from storage
   */
  static getLogs(): AuditLogEntry[] {
    try {
      const data = localStorage.getItem(this.STORAGE_KEY);
      if (!data) return [];

      const logs = JSON.parse(data);
      // Convert timestamp strings back to Date objects
      return logs.map((log: any) => ({
        ...log,
        timestamp: new Date(log.timestamp),
      }));
    } catch (error) {
      console.error('Failed to retrieve audit logs:', error);
      return [];
    }
  }

  /**
   * Save audit logs to storage
   */
  static saveLogs(logs: AuditLogEntry[]): void {
    try {
      localStorage.setItem(this.STORAGE_KEY, JSON.stringify(logs));
    } catch (error) {
      console.error('Failed to save audit logs:', error);
      throw new AuditLogError(
        'Failed to persist audit logs',
        'STORAGE_ERROR',
        error
      );
    }
  }

  /**
   * Get the last entry hash for chain verification
   */
  static getLastHash(): string | null {
    return localStorage.getItem(this.LAST_HASH_KEY);
  }

  /**
   * Save the last entry hash
   */
  static saveLastHash(hash: string): void {
    localStorage.setItem(this.LAST_HASH_KEY, hash);
  }

  /**
   * Clear all audit logs (use with caution!)
   */
  static clear(): void {
    localStorage.removeItem(this.STORAGE_KEY);
    localStorage.removeItem(this.LAST_HASH_KEY);
  }
}

/**
 * Audit Log Service
 * Singleton service for managing audit logs
 */
class AuditLogService {
  private config: AuditLogConfig;
  private pendingBatch: AuditLogEntry[] = [];
  private flushTimer: ReturnType<typeof setInterval> | null = null;

  constructor(config: Partial<AuditLogConfig> = {}) {
    this.config = { ...DEFAULT_CONFIG, ...config };

    // Start flush timer if batching is enabled
    if (this.config.enabled && this.config.flushInterval > 0) {
      this.startFlushTimer();
    }
  }

  /**
   * Generate a unique ID for audit log entries
   */
  private generateId(): string {
    return `audit_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
  }

  /**
   * Generate SHA-256 hash of audit log entry
   */
  private async generateHash(entry: AuditLogEntry): Promise<string> {
    const data = JSON.stringify({
      timestamp: entry.timestamp.toISOString(),
      userId: entry.userId,
      action: entry.action,
      resourceType: entry.resourceType,
      resourceId: entry.resourceId,
      success: entry.success,
      previousHash: entry.previousHash || '',
    });

    // Use Web Crypto API for hashing
    const encoder = new TextEncoder();
    const dataBuffer = encoder.encode(data);
    const hashBuffer = await crypto.subtle.digest('SHA-256', dataBuffer);
    const hashArray = Array.from(new Uint8Array(hashBuffer));
    const hashHex = hashArray.map((b) => b.toString(16).padStart(2, '0')).join('');

    return hashHex;
  }

  /**
   * Get browser metadata for audit context
   */
  private getBrowserMetadata(): {
    ipAddress?: string;
    userAgent?: string;
    sessionId?: string;
  } {
    if (!this.config.includeBrowserMetadata) {
      return {};
    }

    return {
      userAgent: navigator.userAgent,
      // IP address would come from backend
      // Session ID would come from auth context
    };
  }

  /**
   * Create a complete audit log entry
   */
  private async createAuditEntry(
    event: PHIAccessEvent,
    level: AuditLogLevel = AuditLogLevel.INFO
  ): Promise<AuditLogEntry> {
    const previousHash = AuditLogStore.getLastHash() || undefined;
    const browserMetadata = this.getBrowserMetadata();

    const entry: AuditLogEntry = {
      id: this.generateId(),
      timestamp: new Date(),
      userId: event.userId,
      action: event.action,
      resourceType: event.resourceType,
      resourceId: event.resourceId,
      classification: event.classification,
      success: event.success,
      errorMessage: event.errorMessage,
      level,
      previousHash,
      ...browserMetadata,
      metadata: event.metadata,
    } as AuditLogEntry;

    // Generate hash for this entry
    entry.entryHash = await this.generateHash(entry);

    return entry;
  }

  /**
   * Add entry to batch and flush if needed
   */
  private async addToBatch(entry: AuditLogEntry): Promise<void> {
    this.pendingBatch.push(entry);

    // Flush immediately if batch is full
    if (this.pendingBatch.length >= this.config.batchSize) {
      await this.flushBatch();
    }
  }

  /**
   * Flush pending batch to storage
   */
  private async flushBatch(): Promise<void> {
    if (this.pendingBatch.length === 0) return;

    try {
      const existingLogs = AuditLogStore.getLogs();
      const allLogs = [...existingLogs, ...this.pendingBatch];

      // Save to storage
      AuditLogStore.saveLogs(allLogs);

      // Update last hash
      const lastEntry = this.pendingBatch[this.pendingBatch.length - 1];
      if (lastEntry.entryHash) {
        AuditLogStore.saveLastHash(lastEntry.entryHash);
      }

      // Clear batch
      this.pendingBatch = [];
    } catch (error) {
      console.error('Failed to flush audit log batch:', error);
      throw new AuditLogError('Failed to persist audit logs', 'FLUSH_ERROR', error);
    }
  }

  /**
   * Start periodic flush timer
   */
  private startFlushTimer(): void {
    this.flushTimer = setInterval(() => {
      this.flushBatch().catch((error) => {
        console.error('Flush timer error:', error);
      });
    }, this.config.flushInterval);
  }

  /**
   * Stop flush timer
   */
  private stopFlushTimer(): void {
    if (this.flushTimer) {
      clearInterval(this.flushTimer);
      this.flushTimer = null;
    }
  }

  /**
   * Log a PHI access event
   * This is the primary method for logging PHI operations
   */
  async logPHIAccess(event: PHIAccessEvent): Promise<void> {
    if (!this.config.enabled) {
      // In development, still log to console
      if (import.meta.env.NODE_ENV === 'development') {
        console.log('[PHI Audit]', event);
      }
      return;
    }

    try {
      const level = event.success
        ? AuditLogLevel.INFO
        : AuditLogLevel.ERROR;

      const entry = await this.createAuditEntry(event, level);
      await this.addToBatch(entry);
    } catch (error) {
      console.error('Failed to log PHI access:', error);
      // Don't throw - audit logging failure shouldn't break the app
    }
  }

  /**
   * Log a general user action (non-PHI)
   */
  async logUserAction(
    userId: string,
    action: string,
    details?: Record<string, any>
  ): Promise<void> {
    // Convert to PHI access event format for consistency
    await this.logPHIAccess({
      userId,
      action: PHIAccessAction.VIEW, // Generic action
      resourceType: AuditResourceType.AUDIT_LOG, // Non-PHI resource
      resourceId: 'system',
      classification: PHIClassification.NON_PHI,
      success: true,
      metadata: { action, ...details },
    });
  }

  /**
   * Query audit logs with filters
   */
  async queryLogs(filters: AuditLogFilters = {}): Promise<AuditLogEntry[]> {
    const allLogs = AuditLogStore.getLogs();

    let filtered = allLogs;

    // Apply filters
    if (filters.userId) {
      filtered = filtered.filter((log) => log.userId === filters.userId);
    }

    if (filters.action) {
      filtered = filtered.filter((log) => log.action === filters.action);
    }

    if (filters.resourceType) {
      filtered = filtered.filter(
        (log) => log.resourceType === filters.resourceType
      );
    }

    if (filters.resourceId) {
      filtered = filtered.filter((log) => log.resourceId === filters.resourceId);
    }

    if (filters.success !== undefined) {
      filtered = filtered.filter((log) => log.success === filters.success);
    }

    if (filters.startDate) {
      filtered = filtered.filter(
        (log) => log.timestamp >= filters.startDate!
      );
    }

    if (filters.endDate) {
      filtered = filtered.filter((log) => log.timestamp <= filters.endDate!);
    }

    if (filters.level) {
      filtered = filtered.filter((log) => log.level === filters.level);
    }

    // Sort by timestamp descending (newest first)
    filtered.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());

    // Apply pagination
    const offset = filters.offset || 0;
    const limit = filters.limit || 100;

    return filtered.slice(offset, offset + limit);
  }

  /**
   * Get audit log statistics
   */
  async getStats(
    startDate?: Date,
    endDate?: Date
  ): Promise<AuditLogStats> {
    const logs = await this.queryLogs({ startDate, endDate });

    const failedAttempts = logs.filter((log) => !log.success).length;
    const phiAccessCount = logs.filter(
      (log) => log.classification && log.classification !== PHIClassification.NON_PHI
    ).length;

    // Find most common resource type
    const resourceTypeCounts = new Map<AuditResourceType, number>();
    logs.forEach((log) => {
      resourceTypeCounts.set(
        log.resourceType,
        (resourceTypeCounts.get(log.resourceType) || 0) + 1
      );
    });

    const topResourceType = Array.from(resourceTypeCounts.entries()).reduce(
      (max, [type, count]) => (count > (max.count || 0) ? { type, count } : max),
      {} as { type?: AuditResourceType; count?: number }
    ).type;

    // Find most common action
    const actionCounts = new Map<PHIAccessAction, number>();
    logs.forEach((log) => {
      actionCounts.set(log.action, (actionCounts.get(log.action) || 0) + 1);
    });

    const topAction = Array.from(actionCounts.entries()).reduce(
      (max, [action, count]) =>
        count > (max.count || 0) ? { action, count } : max,
      {} as { action?: PHIAccessAction; count?: number }
    ).action;

    return {
      totalEntries: logs.length,
      failedAttempts,
      phiAccessCount,
      topResourceType,
      topAction,
      dateRange: {
        start: startDate || new Date(0),
        end: endDate || new Date(),
      },
    };
  }

  /**
   * Verify audit log chain integrity
   */
  async verifyIntegrity(): Promise<{
    valid: boolean;
    errors: string[];
  }> {
    const logs = AuditLogStore.getLogs();
    const errors: string[] = [];

    for (let i = 0; i < logs.length; i++) {
      const log = logs[i];

      // Verify hash
      const expectedHash = await this.generateHash(log);
      if (log.entryHash !== expectedHash) {
        errors.push(
          `Hash mismatch at entry ${i} (${log.id}): expected ${expectedHash}, got ${log.entryHash}`
        );
      }

      // Verify chain
      if (i > 0) {
        const previousLog = logs[i - 1];
        if (log.previousHash !== previousLog.entryHash) {
          errors.push(
            `Chain break at entry ${i} (${log.id}): previous hash mismatch`
          );
        }
      }
    }

    return {
      valid: errors.length === 0,
      errors,
    };
  }

  /**
   * Clean up old logs according to retention policy
   */
  async cleanupOldLogs(): Promise<{ deletedCount: number; archivedCount: number }> {
    const retentionDate = new Date();
    retentionDate.setDate(
      retentionDate.getDate() - this.config.retention.retentionDays
    );

    const allLogs = AuditLogStore.getLogs();
    const oldLogs = allLogs.filter((log) => log.timestamp < retentionDate);
    const keptLogs = allLogs.filter((log) => log.timestamp >= retentionDate);

    let archivedCount = 0;

    // Archive old logs if configured
    if (this.config.retention.archiveBeforeDelete && oldLogs.length > 0) {
      // TODO: Implement archive storage
      // For now, we'll just count them
      archivedCount = oldLogs.length;
    }

    // Save only the logs within retention period
    AuditLogStore.saveLogs(keptLogs);

    return {
      deletedCount: oldLogs.length,
      archivedCount,
    };
  }

  /**
   * Force flush of pending batch
   */
  async flush(): Promise<void> {
    await this.flushBatch();
  }

  /**
   * Shutdown the service and flush pending logs
   */
  async shutdown(): Promise<void> {
    this.stopFlushTimer();
    await this.flushBatch();
  }
}

// Export singleton instance
export const auditLogService = new AuditLogService();

// Export convenience functions
export const logPHIAccess = (event: PHIAccessEvent) =>
  auditLogService.logPHIAccess(event);

export const logUserAction = (
  userId: string,
  action: string,
  details?: Record<string, any>
) => auditLogService.logUserAction(userId, action, details);

export const queryAuditLogs = (filters?: AuditLogFilters) =>
  auditLogService.queryLogs(filters);

export const getAuditStats = (startDate?: Date, endDate?: Date) =>
  auditLogService.getStats(startDate, endDate);

export const verifyAuditIntegrity = () => auditLogService.verifyIntegrity();

export const cleanupAuditLogs = () => auditLogService.cleanupOldLogs();
